import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email: email.trim() });
      toast.success("If your email is authorized, a 6-digit code is on its way.");
      setStep("code");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: email.trim(), code: code.trim() });
      login(data.token, { email: data.email, role: data.role });
      toast.success("Welcome — access granted.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="brand-band lg:w-[46%] px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between text-white">
        <div>
          <p className="text-xs tracking-[0.25em] text-amber-200/90 font-semibold uppercase">
            Wema Bank · Identity & Access Management
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 text-4xl sm:text-5xl font-bold leading-tight"
          >
            Wema Bank IAM<br />Profiling Discovery
          </motion.h1>
          <p className="mt-6 text-white/75 max-w-md text-base leading-relaxed">
            A secure questionnaire to quantify how access profiling is performed today.
          </p>
          <p className="mt-4 text-amber-200/90 max-w-md text-sm leading-relaxed">
            Authorized internal staff use only. This is not a Wema Bank customer login and
            collects no banking credentials, card details, or account information.
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-3 text-white/70 text-sm">
          <ShieldCheck className="w-5 h-5 text-amber-200" />
          Access is by authorized email and a one-time code. No passwords.
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 bg-[var(--parchment)]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="w-full max-w-md card-surface p-8 shadow-sm"
        >
          <div className="mb-6 rounded-lg bg-[#f6f1e6] border-l-4 p-3 text-xs text-neutral-600 leading-relaxed" style={{ borderLeftColor: "var(--gold)" }} data-testid="internal-use-notice">
            <span className="font-semibold" style={{ color: "var(--burgundy)" }}>Internal staff tool.</span> Authorized internal use only — not a Wema Bank customer login. No banking credentials, card or account details are collected.
          </div>
          {step === "email" ? (
            <form onSubmit={requestCode} data-testid="login-email-form">
              <div className="section-badge mb-5"><Mail className="w-5 h-5" /></div>
              <h2 className="text-2xl font-bold" style={{ color: "var(--burgundy)" }}>Sign in to continue</h2>
              <p className="text-sm text-neutral-500 mt-2 mb-6">
                Enter your authorized work email. We'll send a 6-digit code to verify it's you.
              </p>
              <label className="block text-sm font-medium mb-1.5">Email address</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@wemabank.com"
                className="field-input"
              />
              <button data-testid="login-send-code-btn" type="submit" disabled={loading} className="btn btn-primary w-full mt-6">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} data-testid="login-code-form">
              <div className="section-badge mb-5"><ShieldCheck className="w-5 h-5" /></div>
              <h2 className="text-2xl font-bold" style={{ color: "var(--burgundy)" }}>Enter your code</h2>
              <p className="text-sm text-neutral-500 mt-2 mb-6">
                We sent a 6-digit code to <span className="font-medium text-neutral-700">{email}</span>. It expires in 10 minutes.
              </p>
              <label className="block text-sm font-medium mb-1.5">6-digit code</label>
              <input
                data-testid="login-code-input"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="field-input tracking-[0.5em] text-center text-lg"
              />
              <button data-testid="login-verify-btn" type="submit" disabled={loading} className="btn btn-primary w-full mt-6">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enter"}
              </button>
              <button
                type="button"
                data-testid="login-back-btn"
                onClick={() => { setStep("email"); setCode(""); }}
                className="btn btn-outline w-full mt-3"
              >
                Use a different email
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
